import { FC, memo } from 'react'

import { Button, Text } from '@cash-me/react-components'
import Head from 'next/head'
import NextImage, { getImageProps } from 'next/image'
import { Autoplay, EffectFade } from 'swiper/modules'

import { Carousel } from 'components/carousel'
import { Grid, Col } from 'components/grid'
import useWindowSize from 'hooks/useWindowSize'
import { theme } from 'styles/theme'

import { HeroProps } from './hero.model'
import * as S from './styles'
import { TrustBar } from './trust-bar'

const heroCarouselImages: { name: string; objectPosition?: string }[] = [
  { name: 'hero-desktop-smiling-man.jpeg', objectPosition: 'center 30%' },
  { name: 'hero-desktop-drawing.jpeg', objectPosition: 'center 30%' },
  { name: 'hero-desktop-building.jpeg' },
  { name: 'hero-desktop-family.jpg' }
]

const heroCarouselSlides = heroCarouselImages.map(({ name, objectPosition }) => ({
  src: `${process.env.NEXT_PUBLIC_SITE_IMAGES_PREFIX}/images/site/home/hero/${name}`,
  objectPosition
}))

const mobileHeroImage = `${process.env.NEXT_PUBLIC_SITE_IMAGES_PREFIX}/images/site/home/hero/hero-desktop-mobile.png`

const heroPreload = (src: string) =>
  getImageProps({ src, alt: '', fill: true, quality: 100, sizes: '100vw' }).props

const desktopHeroPreloads = heroCarouselSlides.map(({ src }) => heroPreload(src))
const mobileHeroPreload = heroPreload(mobileHeroImage)

const Hero: FC<HeroProps> = memo(({ onClick }) => {
  const { isMobile } = useWindowSize()

  return (
    <S.Hero>
      <Head>
        {desktopHeroPreloads.map((image, index) => (
          <link
            key={`preload-${image.src}`}
            rel="preload"
            as="image"
            href={image.src}
            imageSrcSet={image.srcSet}
            imageSizes={image.sizes}
            media={`(${theme.media.tablet})`}
            {...{ fetchpriority: index === 0 ? 'high' : 'low' }}
          />
        ))}
        <link
          key={`preload-${mobileHeroPreload.src}`}
          rel="preload"
          as="image"
          href={mobileHeroPreload.src}
          imageSrcSet={mobileHeroPreload.srcSet}
          imageSizes={mobileHeroPreload.sizes}
          media={`(${theme.media.phone})`}
          {...{ fetchpriority: 'high' }}
        />
      </Head>

      {!isMobile && (
        <S.Background>
          <Carousel
            navigation={true}
            swiperOptions={{
              modules: [EffectFade, Autoplay],
              effect: 'fade',
              fadeEffect: { crossFade: true },
              speed: 2000,
              autoplay: { delay: 2000, disableOnInteraction: false },
              rewind: true,
              allowTouchMove: false,
              a11y: { enabled: false }
            }}
          >
            {heroCarouselSlides.map(({ src, objectPosition }, index) => (
              <NextImage
                key={src}
                src={src}
                alt={index === 0 ? 'Empréstimo seguro que impulsiona seus sonhos' : ''}
                fill
                quality={100}
                priority={index === 0}
                loading={index === 0 ? undefined : 'eager'}
                sizes="100vw"
                style={{ objectFit: 'cover', objectPosition }}
              />
            ))}
          </Carousel>
          <S.ImageOverlay />
        </S.Background>
      )}

      {isMobile && (
        <S.MobileBackground>
          <NextImage
            src={mobileHeroImage}
            alt="Empréstimo seguro que impulsiona seus sonhos"
            fill
            quality={100}
            loading="eager"
            sizes="100vw"
            style={{ objectFit: 'cover', objectPosition: 'bottom' }}
          />
          <S.MobileImageOverlay />
        </S.MobileBackground>
      )}

      <Grid col={{ phone: 1, tablet: 1, desktop: 1 }}>
        <Col>
          <div>
            <S.TextWrapper>
              <S.MobileDecoration aria-hidden>
                <S.DecorationTriangle type="TriangleRound" draw="fill" color="#fa0890" />
              </S.MobileDecoration>

              <Text as="h1" size="heading1" weight="medium" color="var(--colors-neutral-text-contrast-default)">
                Seus planos impulsionados pelo {''}
                <Text as="span" size="heading1" weight="medium" color="var(--colors-cyan-text-contrast)">
                  crédito certo
                </Text>
              </Text>

              <Text as="p" color="var(--colors-neutral-text-contrast-default)" weight='regular'>
                Empréstimo com Garantia de Imóvel: soluções financeiras personalizadas e rápidas
              </Text>

              <S.SimulateButton>
                <Button color="primary" size="md" onClick={onClick}>
                  Simule agora
                </Button>

                <S.Connector aria-hidden>
                  <S.DecorationTriangle type="TriangleRound" draw="fill" color="#fa0890" />
                </S.Connector>
              </S.SimulateButton>

              {isMobile && (
                <S.MobileTrustBar>
                  <TrustBar />
                </S.MobileTrustBar>
              )}
            </S.TextWrapper>
          </div>
        </Col>
      </Grid>

      {!isMobile && (
        <S.TrustBarWrapper>
          <TrustBar />
        </S.TrustBarWrapper>
      )}
    </S.Hero>
  )
})

Hero.displayName = 'Hero'

export default Hero
